<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import SyncConfigOptions from '@/js/back-office/components/SyncConfigOptions.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetImportTenantConfigOptions,
        useMutationImportTenantConfig,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    onMounted(() => {
        setFocus('#import-tenant-config-tenant');
    });

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const tenants = computed(() => tenantsData.value ?? []);

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);

    const tenantOptions = computed<Option[]>(() =>
        tenants.value.map((t) => ({ name: t.name, value: t.id }))
    );

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };

    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    const { data: optionsData } = useQueryGetImportTenantConfigOptions(selectedTenantId);
    const options = computed(() => optionsData.value ?? []);

    const selectedConfigs = ref<string[]>([]);

    watch(optionsData, (newOptions) => {
        if (newOptions) {
            selectedConfigs.value = newOptions.filter((o) => o.isSelected).map((o) => o.name);
        }
    });

    watch(selectedTenantId, () => {
        selectedConfigs.value = [];
    });

    const { mutateAsync: importTenantConfigMutation, isPending } = useMutationImportTenantConfig();

    const canSubmit = computed(() => !!selectedTenantId.value && selectedConfigs.value.length > 0);

    const submit = async () => {
        if (!canSubmit.value) return;
        await importTenantConfigMutation({
            tenantId: selectedTenantId.value,
            configs: selectedConfigs.value,
        });
        successDialogStore.openDialog({
            message: __('back-office-import-tenant-config-success'),
        });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-import-tenant-config') }}</h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="import-tenant-config-tenant"
                        :label="__('back-office-import-tenant-config-select-tenant')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                    <SyncConfigOptions
                        v-if="selectedTenantId"
                        v-model="selectedConfigs"
                        :options="options"
                    />
                </FormGroup>
                <div class="mt-8 flex flex-col gap-3 w-fit">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{ __('back-office-import-tenant-config-sync') }}
                    </EchoButton>
                </div>
            </form>
        </template>
    </SettingsPageLayout>
</template>
